import React, { useEffect, useState } from 'react'

import ConsoleDialog from './ConsoleDialog'
import NftAssetCard from './NftAssetCard'
import NftPagedList from './NftPagedList'

export default function NftSelectionDialog({
    show,
    mode = 'stake',
    nfts = [],
    metadataById = {},
    loading = false,
    onClose,
    onConfirm,
    onRefresh,
    onAddManual,
    onVisibleTokenIds,
    t = (key) => key,
}) {
    const [selectedTokenId, setSelectedTokenId] = useState('')
    const [manualTokenId, setManualTokenId] = useState('')
    const [manualLoading, setManualLoading] = useState(false)
    const [confirming, setConfirming] = useState(false)
    const [verifiedTokenId, setVerifiedTokenId] = useState('')
    const isStake = mode === 'stake'
    const title = isStake ? t('选择要质押的 NFT') : t('选择要解除质押的 NFT')
    const listLabel = isStake ? t('钱包中的 NFT') : t('已质押 NFT')
    const verifiedNft = verifiedTokenId
        ? {
            name: `NFT #${verifiedTokenId}`,
            metadataStatus: 'idle',
            ...(metadataById[verifiedTokenId] || {}),
            id: verifiedTokenId,
        }
        : null

    useEffect(() => {
        if (!show) return
        setSelectedTokenId('')
        setManualTokenId('')
        setManualLoading(false)
        setConfirming(false)
        setVerifiedTokenId('')
    }, [mode, show])

    const close = () => {
        if (!confirming && !manualLoading) onClose?.()
    }

    const addManual = async () => {
        const tokenId = manualTokenId.trim()
        if (!tokenId || manualLoading) return
        setManualLoading(true)
        try {
            const result = await onAddManual?.(tokenId)
            if (result !== false) {
                const resultTokenId = String(typeof result === 'object' && result?.tokenId ? result.tokenId : tokenId)
                setVerifiedTokenId(resultTokenId)
                setSelectedTokenId(resultTokenId)
                setManualTokenId('')
            }
        } finally {
            setManualLoading(false)
        }
    }

    const confirm = async () => {
        if (!selectedTokenId || confirming) return
        setConfirming(true)
        try {
            const result = await onConfirm?.(selectedTokenId)
            if (result !== false) onClose?.()
        } finally {
            setConfirming(false)
        }
    }

    return <ConsoleDialog
        show={show}
        onClose={close}
        closeLabel={t('关闭')}
        title={title}
        description={isStake ? t('从当前钱包选择一枚 NFT，确认后进入授权与质押流程。') : t('从已质押列表选择一枚 NFT，确认后提交解除质押交易。')}
        icon={isStake ? 'fa-rocket' : 'fa-unlock'}
        className="console-nft-selection-dialog"
        bodyClassName="console-nft-selection-dialog__body"
        footer={<>
            <button type="button" className="console-button" onClick={close} disabled={confirming || manualLoading}>{t('取消')}</button>
            <button type="button" className={isStake ? 'console-button console-button--primary' : 'console-button console-button--danger'} onClick={confirm} disabled={!selectedTokenId || confirming || manualLoading}>
                {confirming ? t('处理中...') : isStake ? t('确认质押') : t('确认解除质押')}
            </button>
        </>}
    >
        {isStake && <div className="console-nft-selection-toolbar">
            <div>
                <label htmlFor="nft-selection-token-id">{t('手动输入 Token ID')}</label>
                <div className="console-nft-selection-manual">
                    <input
                        id="nft-selection-token-id"
                        value={manualTokenId}
                        onChange={(event) => setManualTokenId(event.target.value)}
                        placeholder="0"
                        inputMode="numeric"
                    />
                    <button type="button" className="console-button" onClick={addManual} disabled={!manualTokenId.trim() || manualLoading}>
                        {manualLoading ? t('验证中') : t('验证 Token ID')}
                    </button>
                    {onRefresh && <button type="button" className="console-icon-button console-nft-selection-refresh" onClick={onRefresh} disabled={loading} title={t('刷新钱包 NFT')} aria-label={t('刷新钱包 NFT')}>
                        <i className={`fa ${loading ? 'fa-spinner loading-css' : 'fa-refresh'}`} />
                    </button>}
                </div>
            </div>
        </div>}
        {verifiedNft && <section className="console-nft-verified" aria-label={t('验证结果')}>
            <div className="console-nft-verified__heading">
                <i className="fa fa-check-circle" aria-hidden="true" />
                <strong>{t('已找到 NFT #{id}').replace('{id}', verifiedTokenId)}</strong>
            </div>
            <div className="console-nft-verified__card">
                <NftAssetCard
                    nft={verifiedNft}
                    selected
                    loading={verifiedNft.metadataStatus === 'loading'}
                    onSelect={setSelectedTokenId}
                    t={t}
                />
            </div>
        </section>}
        <section className="console-nft-selection-list" aria-label={listLabel}>
            <div className="console-nft-selection-summary">
                <span>{listLabel}</span>
                <strong>{loading ? '-' : nfts.length}</strong>
            </div>
            {loading
                ? <div className="console-empty-list">{t('加载中')}</div>
                : nfts.length > 0
                    ? <NftPagedList
                        nfts={nfts}
                        metadataById={metadataById}
                        selectedTokenId={selectedTokenId}
                        onSelect={setSelectedTokenId}
                        onVisibleTokenIds={onVisibleTokenIds}
                        label={listLabel}
                        resetKey={`${show}:${mode}`}
                        t={t}
                    />
                    : <div className="console-empty-list">{isStake ? t('暂无可质押NFT') : t('暂无可解除质押的NFT')}</div>}
        </section>
    </ConsoleDialog>
}
